import React, { useCallback, useEffect, useRef, useState } from 'react';
import ListDataWeb from './ListDataWeb';
import EntryDetailWeb from './EntryDetailWeb';
import AttachmentsWeb from './AttachmentsWeb';
import AttachmentSearchResultsWeb from './AttachmentSearchResultsWeb';
import { useSearchEntries } from '@mysimpledb/shared/hooks/useSearchEntries';
import { useSearchAttachments } from '@mysimpledb/shared/hooks/useSearchAttachments';
import { createHandleListClick } from '@mysimpledb/shared/hooks/useUserData';
import { fetchAttachmentsById } from '@mysimpledb/shared/data/fetchAttachmentsById';
import { fetchEntryById } from '@mysimpledb/shared/data/fetchEntryById';
import useGetSharingSettings from '@mysimpledb/shared/hooks/useGetSharingSettings';
import { useUserSettingsContext } from '@mysimpledb/shared/context/UserSettingsContext';
import './MyDataTabsWeb.css';

export default function MyDataTabsWeb({
  userId,
  searchTerm,
  searchType,
  attachmentSearchTerm,
  setAttachmentSearchTerm,
  setSearchTerm,
  setSearchType,
  setMyDataSearchPlaceholder,
  setMyDataSearchMode,
  entryScope = 'all',
}) {
  const [navSource, setNavSource] = useState('tab');
  const [entries, setEntries] = useState([]);
  const [totalEntriesFound, setTotalEntriesFound] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [currentEntryId, setCurrentEntryId] = useState(null);
  const [offset, setOffset] = useState(30);
  const [entryPageNo, setEntryPageNo] = useState(0);
  const [listPageNo, setListPageNo] = useState(0);
  const [listPageClicked, setListPageClicked] = useState(0);
  const token = localStorage.getItem('token');
  const [selectedAttachmentId, setSelectedAttachmentId] = useState(null);
  const [newEntryRequest, setNewEntryRequest] = useState(0);

  const [activeTab, setActiveTab] = useState('entry');
  const searchModeRef = useRef('entries');

  const [currentIndex, setCurrentIndex] = useState(0);

  const INITIAL_FORM = {
    id: null,
    category: '',
    title: '',
    detail: '',
    detailHtml: '',
    isPublic: false,
    keywords: '',
    sharedName: '',
    shareProfilePicture: '',
    profilePicture: userId,
    dataOwnerId: null,
    imageUrls: [],
    updatedAt: null,
  };
  const [form, setForm] = useState(INITIAL_FORM);

  const [dataOwnerId, setDataOwnerId] = useState(null);
  const [files, setFiles] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isEmptySearch, setIsEmptySearch] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const doSearchEntries = useSearchEntries();
  const doSearchAttachments = useSearchAttachments();

  const [attachmentResults, setAttachmentResults] = useState([]);
  const [totalAttachmentsFound, setTotalAttachmentsFound] = useState(0);
  const [isAttachmentSearchActive, setIsAttachmentSearchActive] =
    useState(false);

  const raw = localStorage.getItem('usersSharingWithMe');
  const usersSharingWithMe = JSON.parse(raw);

  const usersSharingMap = (usersSharingWithMe ?? []).reduce((acc, user) => {
    acc[user.user_id] = user;
    return acc;
  }, {});

  const userIdsSharingWithMe = Object.keys(usersSharingMap).map((id) =>
    Number(id)
  );
  const {
    data: sdata,
    loading: sharingLoading,
    error,
    refetch,
  } = useGetSharingSettings(token);

  const { settings: userSettings, loading: userSettingsLoading } =
    useUserSettingsContext();

  const searchSettings = userSettings?.settings || userSettings || {};

  const applyEntryToForm = useCallback(
    (entry) => {
      if (!entry) return;

      setForm({
        id: entry.id,
        category: entry.category,
        title: entry.title,
        keywords: entry.keywords,
        detail: entry.detail,
        detailHtml: entry.detail_html ?? entry.detailHtml ?? '',
        isPublic: entry.is_public ?? entry.isPublic ?? false,
        isSensitive: entry.is_sensitive ?? entry.isSensitive ?? false,
        sharedName: entry.shared_name,
        shareProfilePicture: entry.share_profile_picture,
        profilePicture: entry.profile_picture,
        dataOwnerId: entry.user_id,
        imageUrls: entry.image_urls ?? entry.imageUrls ?? [],
        updatedAt: entry.updated_at ?? entry.updatedAt ?? null,
      });

      setDataOwnerId(entry.user_id);
      setCurrentEntryId(entry.id);
    },
    [setForm]
  );

  const clearAttachmentSearchState = useCallback(() => {
    setAttachmentSearchTerm('');
    setIsAttachmentSearchActive(false);
    setAttachmentResults([]);
    setTotalAttachmentsFound(0);
  }, [setAttachmentSearchTerm]);

  const switchToEntrySearchMode = useCallback(() => {
    searchModeRef.current = 'entries';
    setMyDataSearchMode?.('entries');
    setMyDataSearchPlaceholder?.('Search Entries');
  }, [setMyDataSearchMode, setMyDataSearchPlaceholder]);

  const switchToAttachmentSearchMode = useCallback(() => {
    searchModeRef.current = 'attachments';
    setMyDataSearchMode?.('attachments');
    setMyDataSearchPlaceholder?.('Search Attachments');
  }, [setMyDataSearchMode, setMyDataSearchPlaceholder]);

  const handleTabClick = (tab) => {
    if (tab === 'attachments') {
      switchToAttachmentSearchMode();
      clearAttachmentSearchState();
      setSelectedAttachmentId(null);
      setActiveTab('attachments');
      return;
    }

    switchToEntrySearchMode();
    clearAttachmentSearchState();
    setActiveTab(tab);
  };

  const handleAttachmentEntryClick = async (item) => {
    switchToEntrySearchMode();
    clearAttachmentSearchState();

    setSelectedAttachmentId(null);

    // Selecting an entry from attachment search is a direct jump.
    // Clear entry filters so the selected entry is not constrained by an old entry search.
    setSearchTerm('');
    setSearchType('');
    setNavSource('tab');

    try {
      let page = 0;
      let matchedEntry = null;
      let matchedIndex = -1;
      let matchedData = null;
      let totalPages = 1;

      // Find the entry on its real unfiltered page instead of inserting it at
      // the beginning of the current entries array. This preserves list order.
      while (page < totalPages) {
        const data = await fetchPage(page, {
          resetIndex: false,
          applyFirstEntry: false,
          searchTermOverride: '',
          searchTypeOverride: '',
        });

        const pageEntries = data?.entries || [];
        const index = pageEntries.findIndex(
          (entry) => Number(entry.id) === Number(item.entryId)
        );

        if (index >= 0) {
          matchedEntry = pageEntries[index];
          matchedIndex = index;
          matchedData = data;
          break;
        }

        const total = Number(data?.total || 0);
        totalPages = Math.max(1, Math.ceil(total / offset));
        page += 1;
      }

      if (!matchedEntry || matchedIndex < 0) {
        console.error(
          'Attachment search entry was not found in the unfiltered entry pages:',
          item.entryId
        );
        return;
      }

      // fetchPage has already committed the matching page to entries.
      setListPageNo(page);
      setEntryPageNo(page);
      setCurrentIndex(matchedIndex);
      setCurrentEntryId(matchedEntry.id);
      setTotalEntriesFound(Number(matchedData?.total || 0));
      applyEntryToForm(matchedEntry);
      setActiveTab('entry');
    } catch (err) {
      console.error('Failed to open attachment search entry:', err);
    }
  };

  const handleAttachmentFileClick = async (item) => {
    switchToAttachmentSearchMode();
    clearAttachmentSearchState();

    setSearchTerm('');
    setSearchType('');

    setCurrentEntryId(item.entryId);
    setSelectedAttachmentId(item.attachmentId);

    try {
      const attachedFiles = await fetchAttachmentsById(item.entryId, token);
      setFiles(attachedFiles);

      const index = entries.findIndex(
        (entry) => Number(entry.id) === Number(item.entryId)
      );

      if (index >= 0) {
        setCurrentIndex(index);
      } else {
        const entry = await fetchEntryById(item.entryId, token);
        if (entry) {
          setEntries((prev) => [
            entry,
            ...prev.filter(
              (existing) => Number(existing.id) !== Number(entry.id)
            ),
          ]);
          setCurrentIndex(0);
          applyEntryToForm(entry);
        }
      }
    } catch (err) {
      console.error('Failed to open attachment search file:', err);
      setFiles([]);
    }

    setActiveTab('attachments');
  };

  const fetchPage = useCallback(
    async (
      page,
      {
        resetIndex = true,
        applyFirstEntry = true,
        searchTermOverride = searchTerm,
        searchTypeOverride = searchType,
      } = {}
    ) => {
      const searchSettings = userSettings?.settings || userSettings;

      if (sharingLoading || !sdata || userSettingsLoading || !searchSettings) {
        console.log('skip fetchPage, settings not ready');
        return;
      }

      try {
        const { data, firstEntry } = await doSearchEntries({
          pageNumber: page,
          searchTerm: searchTermOverride,
          searchType: searchTypeOverride,
          searchPayload: userIdsSharingWithMe,
          viewerTypeIds: sdata?.settings?.allowed_type_ids || [],
          viewerSubtypeIds: sdata?.settings?.allowed_subtype_ids || [],

          broadSearchTitle: !!searchSettings.broad_search_title,
          broadSearchKeywords: !!searchSettings.broad_search_keywords,
          broadSearchCategory: !!searchSettings.broad_search_category,
          broadSearchDetail: !!searchSettings.broad_search_detail,
          onlyMine: entryScope === 'mine',

          token,
          itemsPerPage: offset,
        });

        if (
          sharingLoading ||
          !sdata ||
          userSettingsLoading ||
          !searchSettings
        ) {
          console.log('skip fetchPage, settings not ready');
          return;
        }
        setEntries(data.entries);
        setHasLoaded(true);
        setTotalEntriesFound(data.total);

        // If no results, hard-reset view and keep isEditing false
        if (!data.entries || data.entries.length === 0) {
          setIsEmptySearch(true);
          setCurrentIndex(0);
          setCurrentEntryId(null);
          setForm(INITIAL_FORM);
          setFiles([]);
          setIsEditing(false);
          return data;
        }
        setIsEmptySearch(false);

        if (resetIndex) {
          setCurrentIndex(0);
        }

        if (applyFirstEntry && firstEntry) {
          setForm({
            id: firstEntry.id,
            category: firstEntry.category,
            title: firstEntry.title,
            keywords: firstEntry.keywords,
            detail: firstEntry.detail,
            isPublic: firstEntry.is_public,
            sharedName: firstEntry.shared_name,
            shareProfilePicture: firstEntry.share_profile_picture,
            profilePicture: firstEntry.profile_picture,
            dataOwnerId: firstEntry.user_id,
          });

          setDataOwnerId(firstEntry.user_id);
          setCurrentEntryId(firstEntry.id);
        }

        return data;
      } catch (err) {
        console.error('Failed to fetch page:', err);
      }
    },
    [
      token,
      offset,
      setEntries,
      setTotalEntriesFound,
      searchTerm,
      searchType,
      entryScope,
      sdata,
      sharingLoading,
      userSettings,
      userSettingsLoading,
    ]
  );

  const focusEntryAfterSave = useCallback(
    async (savedEntryId) => {
      if (!savedEntryId) return;

      switchToEntrySearchMode();
      clearAttachmentSearchState();

      setCurrentEntryId(savedEntryId);
      setFiles([]);

      setSearchTerm('');
      setSearchType('');
      setListPageNo(0);
      setEntryPageNo(0);
      setNavSource('tab');

      const data = await fetchPage(0, {
        resetIndex: false,
        searchTermOverride: '',
        searchTypeOverride: '',
      });

      const nextEntries = data?.entries || [];
      const index = nextEntries.findIndex(
        (entry) => Number(entry.id) === Number(savedEntryId)
      );

      // Always fetch the complete saved entry.
      const savedEntry = await fetchEntryById(savedEntryId, token);

      if (savedEntry) {
        if (index >= 0) {
          setCurrentIndex(index);
        } else {
          setEntries((prev) => [
            savedEntry,
            ...prev.filter(
              (entry) => Number(entry.id) !== Number(savedEntry.id)
            ),
          ]);
          setCurrentIndex(0);
        }

        applyEntryToForm(savedEntry);
      }

      try {
        const attachedFiles = await fetchAttachmentsById(savedEntryId, token);
        setFiles(attachedFiles);
      } catch (err) {
        setFiles([]);
        console.error('Failed to load saved entry attachments:', err);
      }

      setActiveTab('entry');
    },
    [
      applyEntryToForm,
      clearAttachmentSearchState,
      fetchPage,
      setSearchTerm,
      setSearchType,
      switchToEntrySearchMode,
      token,
    ]
  );

  useEffect(() => {
    // Entry scope is global across Entry, Attachments, and List.
    // Return to the first page whenever the scope changes.
    setListPageNo(0);
    setEntryPageNo(0);
    setListPageClicked(0);
    setCurrentIndex(0);
    setCurrentEntryId(null);
    setSelectedAttachmentId(null);
  }, [entryScope]);

  useEffect(() => {
    if (navSource === 'list') {
      setEntryPageNo(listPageNo);
    }
  }, [navSource]);

  useEffect(() => {
    fetchPage(listPageNo, { resetIndex: false });
  }, [listPageNo]);

  useEffect(() => {
    if (sharingLoading || userSettingsLoading) return;
    if (!sdata || !userSettings) return;

    fetchPage(listPageNo, { resetIndex: false });
  }, [
    sharingLoading,
    userSettingsLoading,
    sdata,
    userSettings,
    listPageNo,
    fetchPage,
  ]);

  useEffect(() => {
    if (sharingLoading || userSettingsLoading) return;
    if (!sdata || !userSettings) return;
    if (searchModeRef.current !== 'entries') return;
    if (!searchTerm || searchTerm.trim() === '') return;

    async function runEntrySearch() {
      setIsEditing(false);
      setCurrentIndex(0);
      setListPageClicked(0);
      clearAttachmentSearchState();

      setNavSource('list');
      setActiveTab('list');
      setListPageNo(0);

      await fetchPage(0);
    }

    runEntrySearch();
  }, [
    searchTerm,
    searchType,
    sharingLoading,
    userSettingsLoading,
    sdata,
    userSettings,
    fetchPage,
    clearAttachmentSearchState,
  ]);

  useEffect(() => {
    if (sharingLoading || userSettingsLoading) return;
    if (!sdata || !userSettings) return;
    if (searchModeRef.current !== 'attachments') return;

    const term = String(attachmentSearchTerm || '').trim();
    if (!term) {
      setIsAttachmentSearchActive(false);
      setAttachmentResults([]);
      setTotalAttachmentsFound(0);
      return;
    }

    async function runAttachmentSearch() {
      setIsEditing(false);
      setCurrentIndex(0);
      setListPageClicked(0);

      try {
        const data = await doSearchAttachments({
          searchTerm: term,
          searchPayload: userIdsSharingWithMe,
          viewerTypeIds: sdata?.settings?.allowed_type_ids || [],
          viewerSubtypeIds: sdata?.settings?.allowed_subtype_ids || [],
          onlyMine: entryScope === 'mine',
          token,
          pageNumber: 0,
          itemsPerPage: offset,
        });

        setAttachmentResults(data.attachments || []);
        setTotalAttachmentsFound(Number(data.total || 0));
        setIsAttachmentSearchActive(true);
      } catch (err) {
        console.error('Attachment search failed:', err);
        setAttachmentResults([]);
        setTotalAttachmentsFound(0);
        setIsAttachmentSearchActive(true);
      }
    }

    runAttachmentSearch();
  }, [
    attachmentSearchTerm,
    sharingLoading,
    userSettingsLoading,
    sdata,
    userSettings,
    doSearchAttachments,
    token,
    offset,
    entryScope,
  ]);

  const handleListClick = createHandleListClick({
    entries,
    setCurrentIndex,
    setCurrentEntryId,
    setListPageClicked,
    setForm,
    token,
    setActiveTab, // optional for mobile
  });

  const refreshAttachments = useCallback(
    async (entryIdOverride = null) => {
      const id = entryIdOverride ?? entries[currentIndex]?.id;

      if (!id) {
        setFiles([]);
        return [];
      }

      try {
        const attachedFiles = await fetchAttachmentsById(id, token);
        setFiles(attachedFiles);

        // Attachment changes can update the parent entry's updated_at, which
        // can move that entry to a different position in the sorted list.
        // Refresh page 0 and then relocate the same entry by ID so the
        // Attachments view stays on the entry the user was working with.
        const data = await fetchPage(0, {
          resetIndex: false,
          applyFirstEntry: false,
        });

        const refreshedEntries = data?.entries || [];
        const refreshedIndex = refreshedEntries.findIndex(
          (entry) => Number(entry.id) === Number(id)
        );

        if (refreshedIndex >= 0) {
          setCurrentIndex(refreshedIndex);
          setCurrentEntryId(id);
          setListPageNo(0);
          setEntryPageNo(0);
        }

        return attachedFiles;
      } catch (err) {
        setFiles([]);
        console.error('Failed to fetch attachments:', err);
        return [];
      }
    },
    [entries, currentIndex, token, fetchPage]
  );

  useEffect(() => {
    const id = entries[currentIndex]?.id;

    if (!id || isEditing) {
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const attachedFiles = await fetchAttachmentsById(id, token);

        if (!cancelled) {
          setFiles(attachedFiles);
        }
      } catch (err) {
        if (!cancelled) {
          setFiles([]);
          console.error('Failed to fetch attachments:', err);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [entries, currentIndex, isEditing, token]);

  const handleNewEntry = () => {
    setCurrentEntryId(null);
    setCurrentIndex(-1);
    setFiles([]);
    setForm(INITIAL_FORM);
    setIsEditing(true);

    setNewEntryRequest((value) => value + 1);
    setActiveTab('entry');
  };

  return (
    <div className="form-wrapper">
      <div className="mydata-tabs-container">
        <div className="tab-buttons">
          <button
            className={activeTab === 'entry' ? 'tab active' : 'tab'}
            onClick={() => handleTabClick('entry')}
          >
            Entry
          </button>

          <button
            className={activeTab === 'attachments' ? 'tab active' : 'tab'}
            onClick={() => handleTabClick('attachments')}
          >
            Attachments
          </button>

          <button
            className={activeTab === 'list' ? 'tab active' : 'tab'}
            onClick={() => handleTabClick('list')}
          >
            List
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'list' && (
            <ListDataWeb
              currentIndex={currentIndex}
              entries={entries}
              fetchPage={fetchPage}
              listPageClicked={listPageClicked}
              listPageNo={listPageNo}
              onNewEntry={handleNewEntry}
              setActiveTab={setActiveTab}
              setCurrentEntryId={setCurrentEntryId}
              setCurrentIndex={setCurrentIndex}
              setEntryPageNo={setEntryPageNo}
              setForm={setForm}
              setListPageClicked={setListPageClicked}
              setListPageNo={setListPageNo}
              setNavSource={setNavSource}
              token={token}
              totalEntriesFound={totalEntriesFound}
              usersSharingMap={usersSharingMap}
              userId={userId}
            />
          )}

          {activeTab === 'entry' && (
            <EntryDetailWeb
              currentEntryId={currentEntryId}
              currentIndex={currentIndex}
              entries={entries}
              entryPageNo={entryPageNo}
              fetchPage={fetchPage}
              files={files}
              focusEntryAfterSave={focusEntryAfterSave}
              form={form}
              handleListClick={handleListClick}
              hasLoaded={hasLoaded}
              isEditing={isEditing}
              isEmptySearch={isEmptySearch}
              itemsPerPage={offset}
              listPageNo={listPageNo}
              newEntryRequest={newEntryRequest}
              searchTerm={searchTerm}
              searchType={searchType}
              setActiveTab={setActiveTab}
              setCurrentEntryId={setCurrentEntryId}
              setCurrentIndex={setCurrentIndex}
              setFiles={setFiles}
              setForm={setForm}
              setIsEditing={setIsEditing}
              setListPageNo={setListPageNo}
              setNewEntryRequest={setNewEntryRequest}
              setSearchTerm={setSearchTerm}
              setSearchType={setSearchType}
              setTotalEntriesFound={setTotalEntriesFound}
              token={token}
              totalEntriesFound={totalEntriesFound}
              userId={userId}
            />
          )}

          {activeTab === 'attachments' && isAttachmentSearchActive ? (
            <AttachmentSearchResultsWeb
              results={attachmentResults}
              totalAttachmentsFound={totalAttachmentsFound}
              onEntryClick={handleAttachmentEntryClick}
              onAttachmentClick={handleAttachmentFileClick}
            />
          ) : activeTab === 'attachments' ? (
            <AttachmentsWeb
              entryId={entries[currentIndex]?.id ?? currentEntryId ?? null}
              files={files}
              token={token}
              userId={userId}
              selectedAttachmentId={selectedAttachmentId}
              onRefresh={refreshAttachments}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
